import { ensure, getBestPrecision } from './utils';

// every heal of Jailer's P4 cast heals for this percent (pre-reduction)
export const JAILER_HEAL_TICK = 0.02;
// jailer begins P4 at this health
export const JAILER_P4_START_HEALTH_FRAC = 0.1;
// jailer heals by this much total health in secret mythic phase (can be mitigated by Mortal Strike effects)
export const JAILER_HEAL_FRAC = JAILER_HEAL_TICK * 9;

export type TPhaseMetric = 'overallPercent' | 'elapsedMs';
export type TPhaseValue = number | string;

export type TPhaseInput = {
  overallPercent: number;
  elapsedMs?: number | null;
};

export type TPhaseProgress = {
  phase: TPhaseValue | null;
  phaseLabel: string | null;
  bossPercent: number;
};

export type TPublicPhaseValue = number | null;

export type TPhaseSegment = {
  phase: TPhaseValue;
  phaseLabel: string;
  metric: TPhaseMetric;
  minValue: number;
  maxValue: number | null;
  minExclusive: boolean;
  maxExclusive: boolean;
};

type TPhaseDef = {
  phase: TPhaseValue | null;
  metric: TPhaseMetric;
  minValue: number;
  maxValue?: number | null;
  minExclusive?: boolean;
  maxExclusive?: boolean;
  matchesPhase?: (input: TPhaseInput, phaseDef: TPhaseDef) => boolean;
  calculateBossPercent: (input: TPhaseInput, phaseDef: TPhaseDef) => number;
  formatPhaseLabel?: (phaseDef: TPhaseDef, input?: TPhaseInput) => string | null;
};

function clampPercent(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function getMetricValue(input: TPhaseInput, metric: TPhaseMetric): number | null {
  if (metric === 'overallPercent') {
    return input.overallPercent;
  }

  return typeof input.elapsedMs === 'number' ? input.elapsedMs : null;
}

function normalizePhaseInput(input: TPhaseInput | number): TPhaseInput {
  if (typeof input === 'number') {
    return { overallPercent: input };
  }

  return input;
}

function isWithinPhaseRange(metricValue: number, phaseDef: TPhaseDef): boolean {
  if (phaseDef.minExclusive ? metricValue <= phaseDef.minValue : metricValue < phaseDef.minValue) {
    return false;
  }

  if (typeof phaseDef.maxValue === 'number') {
    if (phaseDef.maxExclusive ? metricValue >= phaseDef.maxValue : metricValue > phaseDef.maxValue) {
      return false;
    }
  }

  return true;
}

function getPhaseLabel(phaseDef: TPhaseDef, input?: TPhaseInput): string | null {
  if (phaseDef.formatPhaseLabel) {
    return phaseDef.formatPhaseLabel(phaseDef, input);
  }

  if (phaseDef.phase === null) {
    return null;
  }

  if (typeof phaseDef.phase === 'number') {
    return `P${phaseDef.phase}`;
  }

  return phaseDef.phase.toUpperCase();
}

function matchesMetricRange(input: TPhaseInput, phaseDef: TPhaseDef): boolean {
  const metricValue = getMetricValue(input, phaseDef.metric);
  if (metricValue === null) {
    return false;
  }

  return isWithinPhaseRange(metricValue, phaseDef);
}

function matchesPhaseDefinition(input: TPhaseInput, phaseDef: TPhaseDef): boolean {
  if (phaseDef.matchesPhase) {
    return phaseDef.matchesPhase(input, phaseDef);
  }

  return matchesMetricRange(input, phaseDef);
}

function calculatePhaseProgress(phaseDefs: TPhaseDef[], input: TPhaseInput): TPhaseProgress | null {
  for (const phaseDef of phaseDefs) {
    if (!matchesPhaseDefinition(input, phaseDef)) {
      continue;
    }

    return {
      phase: phaseDef.phase,
      phaseLabel: getPhaseLabel(phaseDef, input),
      bossPercent: clampPercent(phaseDef.calculateBossPercent(input, phaseDef))
    };
  }

  return null;
}

function ensurePhaseMaxValue(phaseDef: TPhaseDef): number {
  ensure(typeof phaseDef.maxValue === 'number', 'Phase definition is missing maxValue');
  return phaseDef.maxValue as number;
}

function formatRangeProgress(value: number, minValue: number, maxValue: number): number {
  const span = maxValue - minValue;
  ensure(span > 0, 'Phase span must be positive');
  return (value - minValue) / span;
}

const DIMENSIUS_PHASE_1_SPAN = 0.25; // Phase 1: 1.0 to 0.75 (range: 0.25)
const DIMENSIUS_PHASE_2_1_SPAN = 0.15; // Phase 2.1: 0.75 to 0.60 (range: 0.15)
const DIMENSIUS_PHASE_2_2_SPAN = 0.15; // Phase 2.2: 0.60 to 0.45 (range: 0.15)
const DIMENSIUS_PHASE_3_SPAN = 0.45; // Phase 3: 0.45 to 0.00 (range: 0.45)

ensure(
  DIMENSIUS_PHASE_1_SPAN + DIMENSIUS_PHASE_2_1_SPAN + DIMENSIUS_PHASE_2_2_SPAN + DIMENSIUS_PHASE_3_SPAN - 1.0 <
    Number.EPSILON,
  'Dimensius phase spans must sum to exactly 1.0 (adjusted for floating point precision)'
);

const DIMENSIUS_PHASES: TPhaseDef[] = [
  {
    phase: 1,
    metric: 'overallPercent',
    minValue: 1.0 - DIMENSIUS_PHASE_1_SPAN,
    maxValue: 1.0,
    calculateBossPercent: (input, phaseDef) =>
      formatRangeProgress(input.overallPercent, phaseDef.minValue, ensurePhaseMaxValue(phaseDef))
  },

  {
    phase: 2.1,
    metric: 'overallPercent',
    minValue: 1.0 - DIMENSIUS_PHASE_1_SPAN - DIMENSIUS_PHASE_2_1_SPAN,
    maxValue: 1.0 - DIMENSIUS_PHASE_1_SPAN,
    maxExclusive: true,
    calculateBossPercent: (input, phaseDef) =>
      formatRangeProgress(input.overallPercent, phaseDef.minValue, ensurePhaseMaxValue(phaseDef))
  },

  {
    phase: 2.2,
    metric: 'overallPercent',
    minValue: 1.0 - DIMENSIUS_PHASE_1_SPAN - DIMENSIUS_PHASE_2_1_SPAN - DIMENSIUS_PHASE_2_2_SPAN,
    maxValue: 1.0 - DIMENSIUS_PHASE_1_SPAN - DIMENSIUS_PHASE_2_1_SPAN,
    maxExclusive: true,
    calculateBossPercent: (input, phaseDef) =>
      formatRangeProgress(input.overallPercent, phaseDef.minValue, ensurePhaseMaxValue(phaseDef))
  },

  {
    phase: 3,
    metric: 'overallPercent',
    minValue: 0,
    maxValue: DIMENSIUS_PHASE_3_SPAN,
    maxExclusive: true,
    calculateBossPercent: (input) => input.overallPercent / DIMENSIUS_PHASE_3_SPAN
  }
];

const NEXUS_KING_PHASES: TPhaseDef[] = [
  // Phase 1: 1.0 to 0.67 (range: 0.33)
  {
    phase: 1,
    metric: 'overallPercent',
    minValue: 0.67,
    maxValue: 1.0,
    calculateBossPercent: (input) => (input.overallPercent - 0.67) / 0.33
  },

  // Phase 2: 0.67 to 0.34 (range: 0.33)
  {
    phase: 2,
    metric: 'overallPercent',
    minValue: 0.34,
    maxValue: 0.67,
    maxExclusive: true,
    calculateBossPercent: (input) => (input.overallPercent - 0.34) / 0.33
  },

  // Phase 3: 0.34 to 0.00 (range: 0.34)
  {
    phase: 3,
    metric: 'overallPercent',
    minValue: 0,
    maxValue: 0.34,
    maxExclusive: true,
    calculateBossPercent: (input) => input.overallPercent / 0.34
  }
];

const jailerAdditionalHealth = JAILER_HEAL_FRAC * 1000;
const jailerTotalHealth = jailerAdditionalHealth + 1000;
const jailerThresholdP4 = (JAILER_P4_START_HEALTH_FRAC * 1000 + jailerAdditionalHealth) / jailerTotalHealth;

// Calculate the threshold where bossPercent = 0.25 (transition from null to Phase 3)
// 0.25 = (overallPercent * jailerTotalHealth - jailerAdditionalHealth) / 1000
// 250 = overallPercent * jailerTotalHealth - jailerAdditionalHealth
// overallPercent = (250 + jailerAdditionalHealth) / jailerTotalHealth
const jailerThresholdNullToP3 = (250 + jailerAdditionalHealth) / jailerTotalHealth;

const JAILER_PHASES: TPhaseDef[] = [
  // Null phase: from jailerThresholdNullToP3 to 1.0
  {
    phase: null,
    metric: 'overallPercent',
    minValue: jailerThresholdNullToP3,
    maxValue: 1.0,
    calculateBossPercent: (input) => (input.overallPercent * jailerTotalHealth - jailerAdditionalHealth) / 1000
  },

  // Phase 3: from jailerThresholdP4 to jailerThresholdNullToP3
  {
    phase: 3,
    metric: 'overallPercent',
    minValue: jailerThresholdP4,
    maxValue: jailerThresholdNullToP3,
    maxExclusive: true,
    calculateBossPercent: (input) => (input.overallPercent * jailerTotalHealth - jailerAdditionalHealth) / 1000
  },

  // Phase 4: from 0.0 to jailerThresholdP4
  {
    phase: 4,
    metric: 'overallPercent',
    minValue: 0,
    maxValue: jailerThresholdP4,
    maxExclusive: true,
    calculateBossPercent: (input) => (input.overallPercent * jailerTotalHealth) / 1000
  }
];

const CROWN_OF_THE_COSMOS_PHASES: TPhaseDef[] = [
  // Phase 1: 1.0 to 0.8 (adds)
  {
    phase: 1,
    metric: 'overallPercent',
    minValue: 0.8,
    maxValue: 1.0,
    calculateBossPercent: (input) => (input.overallPercent - 0.8) / 0.2
  },

  // Phase 2: 0.8 to 0.4 (Alleria + Rift alive)
  {
    phase: 2,
    metric: 'overallPercent',
    minValue: 0.4,
    maxValue: 0.8,
    maxExclusive: true,
    calculateBossPercent: (input) => (input.overallPercent - 0.4) / 0.4
  },

  // Phase 3: 0.4 to 0.0 (Alleria only)
  {
    phase: 3,
    metric: 'overallPercent',
    minValue: 0,
    maxValue: 0.4,
    maxExclusive: true,
    calculateBossPercent: (input) => input.overallPercent / 0.4
  }
];

const RAGNAROS_PHASES: TPhaseDef[] = [
  // We're in P1-3 (above 50%)
  {
    phase: null,
    metric: 'overallPercent',
    minValue: 0.5 + Number.EPSILON,
    maxValue: 1.0,
    calculateBossPercent: (input) => (input.overallPercent - 0.5) * 2
  },

  // We're in P4 (at or below 50%)
  {
    phase: 4,
    metric: 'overallPercent',
    minValue: 0,
    maxValue: 0.5,
    calculateBossPercent: (input) => input.overallPercent * 2
  }
];

const MIDNIGHT_FALLS_P4_START_HEALTH_FRAC = 0.2;
const MIDNIGHT_FALLS_MYTHIC_P1_TO_3_HEALTH_FRAC = 1.0 - MIDNIGHT_FALLS_P4_START_HEALTH_FRAC;

function buildMidnightFallsTimePhases(difficulty: string, matchesPhase?: TPhaseDef['matchesPhase']): TPhaseDef[] {
  const I1_START = 190000;
  const P2_START = 232000;
  const P3_START = 330000;

  const calculateBossPercent =
    difficulty === 'mythic'
      ? (input: TPhaseInput) =>
          (input.overallPercent - MIDNIGHT_FALLS_P4_START_HEALTH_FRAC) / MIDNIGHT_FALLS_MYTHIC_P1_TO_3_HEALTH_FRAC
      : (input: TPhaseInput) => input.overallPercent;

  return [
    // Phase 1: pull start to 03:10
    {
      phase: 1,
      metric: 'elapsedMs',
      minValue: 0,
      maxValue: I1_START,
      matchesPhase,
      calculateBossPercent
    },

    // Intermission 1: 03:10 - 3:52
    {
      phase: 'i1',
      metric: 'elapsedMs',
      minValue: I1_START,
      minExclusive: true,
      maxValue: P2_START,
      matchesPhase,
      calculateBossPercent
    },

    // Phase 2: 3:52 - 5:30
    {
      phase: 2,
      metric: 'elapsedMs',
      minValue: P2_START,
      minExclusive: true,
      maxValue: P3_START,
      matchesPhase,
      calculateBossPercent
    },

    // Phase 3: 5:30 to 8:42
    {
      phase: 3,
      metric: 'elapsedMs',
      minValue: P3_START,
      minExclusive: true,
      matchesPhase,
      calculateBossPercent
    }
  ];
}

const MIDNIGHT_FALLS_PHASES: TPhaseDef[] = buildMidnightFallsTimePhases('heroic');

const MYTHIC_MIDNIGHT_FALLS_PHASES: TPhaseDef[] = [
  // P1-I1-P3 are time-based until the secret phase 4 health threshold is reached.
  ...buildMidnightFallsTimePhases(
    'mythic',
    (input, phaseDef) =>
      input.overallPercent >= MIDNIGHT_FALLS_P4_START_HEALTH_FRAC && matchesMetricRange(input, phaseDef)
  ),
  // Phase 4 (secret): 20% to 0%
  {
    phase: 4,
    metric: 'overallPercent',
    minValue: 0,
    maxValue: MIDNIGHT_FALLS_P4_START_HEALTH_FRAC,
    maxExclusive: true,
    calculateBossPercent: (input) => input.overallPercent / MIDNIGHT_FALLS_P4_START_HEALTH_FRAC
  }
];

function getBossPhases(difficulty: string, bossSlug: string): TPhaseDef[] | null {
  if (bossSlug === 'dimensius') {
    return DIMENSIUS_PHASES;
  } else if (bossSlug === 'nexus-king-salhadaar') {
    return NEXUS_KING_PHASES;
  } else if (difficulty === 'mythic' && (bossSlug === 'the-jailer' || bossSlug === 'fated-the-jailer')) {
    return JAILER_PHASES;
  } else if (bossSlug === 'crown-of-the-cosmos') {
    return CROWN_OF_THE_COSMOS_PHASES;
  } else if (difficulty === 'mythic' && bossSlug === 'midnight-falls') {
    return MYTHIC_MIDNIGHT_FALLS_PHASES;
  } else if (difficulty === 'heroic' && bossSlug === 'ragnaros') {
    return RAGNAROS_PHASES;
  } else if (bossSlug === 'midnight-falls') {
    return MIDNIGHT_FALLS_PHASES;
  }

  return null;
}

export function formatPhaseDisplay(
  phaseProgress: TPhaseProgress,
  formatPercent: (bossPercent: number) => string
): string {
  const percentText = formatPercent(phaseProgress.bossPercent);
  return phaseProgress.phaseLabel ? `${percentText} ${phaseProgress.phaseLabel}` : percentText;
}

export function getPublicPhaseValue(phase: TPhaseValue | null): TPublicPhaseValue {
  if (typeof phase === 'string') {
    const intermissionMatch = /^i(\d+(?:\.\d+)?)$/i.exec(phase);
    if (intermissionMatch) {
      return Number(intermissionMatch[1]) + 0.5;
    }
  }

  return typeof phase === 'number' ? phase : null;
}

export function getPhaseProgress(
  difficulty: string,
  bossSlug: string,
  input: TPhaseInput | number
): TPhaseProgress | null {
  const phaseDefs = getBossPhases(difficulty, bossSlug);
  if (phaseDefs) {
    return calculatePhaseProgress(phaseDefs, normalizePhaseInput(input));
  }

  return null;
}

export function getPhaseProgressDisplay(difficulty: string, bossSlug: string, input: TPhaseInput | number): string {
  const normalizedInput = normalizePhaseInput(input);
  const phaseProgress = getPhaseProgress(difficulty, bossSlug, normalizedInput);
  if (!phaseProgress) {
    return `${getBestPrecision(normalizedInput.overallPercent * 100)}%`;
  }

  return formatPhaseDisplay(phaseProgress, (bossPercent) => `${getBestPrecision(bossPercent * 100)}%`);
}

export function getPhaseSegments(difficulty: string, bossSlug: string): TPhaseSegment[] {
  const phaseDefs = getBossPhases(difficulty, bossSlug);
  if (phaseDefs) {
    return phaseDefs
      .filter((phaseDef): phaseDef is TPhaseDef & { phase: TPhaseValue } => phaseDef.phase !== null)
      .map((phaseDef) => ({
        phase: phaseDef.phase,
        phaseLabel: getPhaseLabel(phaseDef) || '',
        metric: phaseDef.metric,
        minValue: phaseDef.minValue,
        maxValue: typeof phaseDef.maxValue === 'number' ? phaseDef.maxValue : null,
        minExclusive: Boolean(phaseDef.minExclusive),
        maxExclusive: Boolean(phaseDef.maxExclusive)
      }));
  }

  return [];
}
